import { parseDate } from "../Helpers";
import { AppointmentsApi } from "../../Api/AppointmentsApi";
import {
  eachDayOfInterval,
  endOfMonth,
  startOfDay,
  startOfMonth,
} from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { FormStateType } from "../Helpers/formTypes";
import { GtmService } from "../GtmService";
import { useBookingContext } from "../../BookingButton/BookingContext/BookingContext";
import { IntervalDay } from "../Steps/AppointmentStep/AppointmentStep";
import { CenterDto } from "@apiModels/CenterDto.csharp";
import { filterAppointments } from "../Helpers/filterAppointments";
import { newDateInOsloTimeZone } from "../Helpers/timeZoneHelpers";
import { DoctorDto } from "@apiModels/DoctorDto.csharp";
import { AppointmentDto } from "@apiModels/AppointmentDto.csharp";

const appointmentApi = new AppointmentsApi();

export const useInterval = (
  state: FormStateType,
  activeMonth: Date,
  autoQueryNextMonth: () => void,
  autoQueryRepeat: number,
  filteredDoctors: DoctorDto[],
  selectedDay: Date
) => {
  const [interval, setInterval] = useState<IntervalDay[]>([]);
  const [filteredAppointments, setFilteredAppointments] = useState<
    AppointmentDto[]
  >([]);
  const context = useBookingContext();

  const { appointments, loadingAppointments, error, warning, failedCenters } =
    useAppointments(
      state,
      activeMonth,
      autoQueryNextMonth,
      context.dipsAutoQueryMaxRepeats,
      autoQueryRepeat,
      filteredDoctors
    );

  const appointmentsForSelectedDay = useMemo(() => {
    if (!selectedDay) return [];
    return filteredAppointments.filter(
      (a) => new Date(a.startTime).toDateString() === selectedDay.toDateString()
    );
  }, [filteredAppointments, selectedDay]);

  const getInterval = (appointments: AppointmentDto[]) => {
    if (!appointments.length) {
      return [];
    }

    return eachDayOfInterval({
      start: new Date(appointments[0].startTime),
      end: new Date(appointments[appointments.length - 1].startTime),
    }).map<IntervalDay>((date) => {
      return {
        date,
        hasAppointments: appointments.some(
          (a) => startOfDay(new Date(a.startTime)).getTime() === date.getTime()
        ),
      };
    });
  };

  useEffect(() => {
    const filteredAppointments = filterAppointments(
      state,
      appointments,
      filteredDoctors
    );
    setFilteredAppointments(filteredAppointments);
    setInterval(getInterval(filteredAppointments));
  }, [
    state.doctor,
    state.location,
    appointments,
    state.relatedServiceSetups,
    state.showRelatedServicesMap,
  ]);

  return {
    interval,
    filteredAppointments,
    loadingAppointments,
    autoQueryRepeat,
    error,
    warning,
    failedCenters,
    appointmentsForSelectedDay,
  };
};

const useAppointments = (
  state: FormStateType,
  activeMonth: Date,
  autoQueryNextMonth: () => void,
  dipsAutoQueryMaxRepeats: number,
  autoQueryRepeat: number,
  filteredDoctors: DoctorDto[]
) => {
  const [appointments, setAppointments] = useState<AppointmentDto[]>();
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [failedCenters, setFailedCenters] = useState<CenterDto[]>([]);

  const tryFetchAppointments = async () => {
    const savedAppointments =
      appointments?.filter(
        (a) =>
          startOfMonth(new Date(a.startTime)).getTime() == activeMonth.getTime()
      ) ?? [];
    if (savedAppointments.length == 0) {
      if (loadingAppointments && autoQueryRepeat === 0) return;
      setLoadingAppointments(true);
      try {
        const { startDate, endDate } = getPeriod(activeMonth);
        const response = await getAppointments(
          state.service.id,
          startDate,
          endDate
        );
        if (response) {
          setWarning(response.cannotLoadSomeAppointments);
          setFailedCenters(response.failedCenters);
          const newAppointments = [
            ...(appointments ?? []),
            ...response.appointments,
          ];
          setAppointments(newAppointments);
          setLoadingAppointments(false);
          return {
            appointments: newAppointments,
            failedCenters: response.failedCenters,
          };
        }
      } catch (err: any) {
        setError(err.message);
        GtmService.appointmentsLoadingError(
          state.service.name,
          state.specializationName,
          state.location.name,
          err.message
        );
      }
    }

    setLoadingAppointments(false);
    return {
      appointments,
      failedCenters,
    };
  };

  useEffect(() => {
    if (activeMonth) {
      (async () => {
        const fetchedAppointments = await tryFetchAppointments();
        const filtered = filterAppointments(
          state,
          fetchedAppointments?.appointments,
          filteredDoctors
        );
        if (
          !filtered.length &&
          dipsAutoQueryMaxRepeats > autoQueryRepeat &&
          fetchedAppointments?.failedCenters.every(
            (c) => c.id !== state.location?.id
          )
        ) {
          autoQueryNextMonth();
        }
      })();
    }
  }, [activeMonth, state.doctor, state.location]);

  return {
    appointments,
    loadingAppointments,
    autoQueryRepeat,
    error,
    warning,
    failedCenters,
  };
};

const getAppointments = async (
  serviceId: string,
  startDate: Date,
  endDate: Date
) => {
  const response = await appointmentApi.getByDateRange(
    serviceId,
    parseDate(startDate),
    parseDate(endDate)
  );

  return {
    cannotLoadSomeAppointments: response.cannotLoadSomeAppointments ?? false,
    failedCenters: response.failedCenters ?? [],
    appointments: response.appointments.map((a) => ({
      ...a,
      startTime: new Date(a.startTime),
      endTime: new Date(a.endTime),
    })),
  };
};

const getPeriod = (selectedMonth: Date) => {
  const startDate =
    selectedMonth.getTime() > newDateInOsloTimeZone().getTime()
      ? startOfMonth(selectedMonth)
      : startOfDay(newDateInOsloTimeZone());

  const endDate = endOfMonth(startDate);

  return { startDate, endDate };
};
